import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { KeyedMutator } from 'swr';

import { KnownExternalServiceId } from '@rikstv/play-common/src/forces/externalService/externalServiceIds';
import { commonSignUp, commonUpgrade } from '@rikstv/play-common/src/forces/loadUserInfo/actions';
import { commonRoutes } from '@rikstv/play-common/src/router/router.path';

import {
  DefaultProviderVariant,
  ProviderVariant,
  SuperCoverPageMovie,
  SuperCoverPageSeries,
} from '../contentStack.types';

import { useModalPlayerLogic } from './useModalPlayerLogic';
import { useOnPlayExternal } from './useOnPlayExternal';

export const KNOWN_LABELS = {
  actors: 'Skuespillere',
  director: 'Regissør',
  genre: 'Sjangere',
} as const;

export type GetExternalServiceStatusFn = (serviceId: KnownExternalServiceId) => {
  activated: boolean;
  activationPageUrl?: string;
};

export const useContentStackEventHandlers = (
  defaultVariant: DefaultProviderVariant,
  mutate: KeyedMutator<SuperCoverPageMovie> | KeyedMutator<SuperCoverPageSeries>
) => {
  const [isVariantModalOpen, setVariantModalOpen] = useState(false);
  const [isTransactionModalOpen, setTransactionModalOpen] = useState(false);
  const [isPinModalOpen, setIsPinModalOpen] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState<ProviderVariant | undefined>(undefined);
  const onCloseVariantModal = useCallback(() => setVariantModalOpen(false), []);
  const onOpenVariantModal = useCallback(() => setVariantModalOpen(true), []);
  const onCloseTransactionlModal = useCallback(() => setTransactionModalOpen(false), []);
  const onCloseParentalModal = useCallback(() => setIsPinModalOpen(false), []);
  const onOpenParentalModal = useCallback((variant: ProviderVariant) => {
    //Set the chosen variant and open pin modal
    setSelectedVariant(variant);
    setIsPinModalOpen(true);
  }, []);
  const { assetId, setAssetId, onCloseModalPlayer } = useModalPlayerLogic(defaultVariant, onOpenParentalModal);

  const deferredMutate = useCallback(() => setTimeout(mutate, 1500), [mutate]);
  const onExternalServiceLaunched = useOnPlayExternal(deferredMutate);

  const onClickPrimaryAction = useOnClickPrimaryAction(
    defaultVariant,
    setAssetId,
    onOpenVariantModal,
    onOpenParentalModal
  );

  const onAccessGranted = useCallback(() => {
    if (selectedVariant?.behavior === 'PlayAsset') {
      setAssetId(selectedVariant.tracking.elementId);
      return;
    }

    if (selectedVariant?.behavior === 'PlayExternalAsset') {
      onExternalServiceLaunched(selectedVariant);
      return;
    }

    if (selectedVariant?.behavior === 'RentAsset') {
      setTransactionModalOpen(true);
    }
    if (selectedVariant?.behavior === 'PurchaseAsset') {
      setTransactionModalOpen(true);
    }
  }, [selectedVariant, setAssetId, onExternalServiceLaunched]);

  const onSelectProvider = useOnSelectVariant(onCloseVariantModal, onOpenParentalModal);
  const onAssetTransaction = () => {
    deferredMutate();
    onCloseTransactionlModal();
    return;
  };

  return {
    // state
    assetId,
    isVariantModalOpen,
    isTransactionModalOpen,
    isPinModalOpen,
    selectedVariant,
    // events
    onSelectProvider,
    onClickPrimaryAction,
    onCloseVariantModal,
    onCloseTransactionlModal,
    onCloseParentalModal,
    onCloseModalPlayer,
    onAssetTransaction,
    onAccessGranted,
  };
};

export type OnSelectVariant = ReturnType<typeof useOnSelectVariant>;
const useOnSelectVariant = (
  closeVariantSelectionModal: () => void,
  openParentalPinModal: (variant: ProviderVariant) => void
) => {
  const navigate = useNavigate();
  // reuse variant-selection element both in list and modal that opens on play
  // only render if more than 1 variant
  return useCallback(
    (variant: ProviderVariant) => {
      const { behavior } = variant;
      closeVariantSelectionModal();
      if (behavior === 'PlayAsset' || 'PlayExternalAsset') {
        openParentalPinModal(variant);
      } else if (behavior === 'PlayChannel') {
        navigate(commonRoutes.watchLive(variant._links.channel.id));
      }
    },
    [closeVariantSelectionModal, navigate, openParentalPinModal]
  );
};

import { isModifierKeyPressed } from '@rikstv/play-common/src/utils/eventHandler/isModifierKeyPressed';

import { push } from '../../../utils/analytics/googleTagManager';

const useOnClickPrimaryAction = (
  defaultVariant: DefaultProviderVariant,
  setAssetId: (id: string) => void,
  openVariantModal: () => void,
  openParentalPinModal: (variant: ProviderVariant) => void
) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  return useCallback(
    (event?: React.MouseEvent): boolean => {
      // Respect shift/control/alt clicks
      if (isModifierKeyPressed(event)) {
        return true;
      }
      // Prevent default link etc
      event?.preventDefault();

      // use explicit return type 👆 to get exhaustive check on switch-statement
      switch (defaultVariant?.behavior) {
        case 'NoAction':
          return true;
        case 'PlayAsset':
          openParentalPinModal(defaultVariant);
          return true;
        case 'PlayChannel':
          navigate(commonRoutes.watchLive(defaultVariant._links.channel.id));
          return true;

        case 'PlayExternalAsset':
          openParentalPinModal(defaultVariant);
          return true;

        case 'ActivateExternalService':
          const link = defaultVariant._links.externalPlayback.activationPageUrl;
          const target = link?.startsWith('/') ? '_self' : '_blank';
          window.open(link, target);
          return true;

        case 'UpgradeSubscription':
          push({
            event: 'user_interaction',
            event_params: {
              context: location.pathname.toString(),
              event_group: 'shopper',
              event_type: 'upgrade from content',
            },
          });
          dispatch(commonUpgrade({ fromAssetId: defaultVariant.tracking.elementId }));
          return true;

        case 'SignIn':
          push({
            event: 'user_interaction',
            event_params: {
              context: location.pathname.toString(),
              event_group: 'shopper',
              event_type: 'sign-up from content',
            },
          });
          dispatch(commonSignUp({ fromAssetId: defaultVariant.tracking.elementId }));
          return true;
        case 'RentAsset':
          openParentalPinModal(defaultVariant);
          return true;
        case 'PurchaseAsset':
          openParentalPinModal(defaultVariant);
          return true;
        case 'ShowVariants':
        case undefined:
          openVariantModal();
          return true;
      }
    },
    [defaultVariant, openParentalPinModal, navigate, dispatch, openVariantModal]
  );
};
